// components/shared/Table.jsx
export function Table({
  columns = [],
  data = [],
  onRowClick,
  primaryColor = "#ea580c",
  emptyMessage = "No hay datos disponibles",
  rowKey, // string | (row) => string/number
}) {
  const getRowKey = (row, idx) => {
    if (typeof rowKey === "function") return rowKey(row);
    if (typeof rowKey === "string" && row?.[rowKey] != null) return row[rowKey];
    return idx;
  };

  const clickable = typeof onRowClick === "function";

  return (
    <div
      className="rounded-2xl border bg-slate-900/55 backdrop-blur-xl overflow-hidden
                 shadow-[0_20px_60px_-35px_rgba(0,0,0,0.7)]"
      style={{ borderColor: `${primaryColor}22` }}
    >
      <div className="overflow-x-auto">
        <table className="w-full">
          {/* Sticky header */}
          <thead
            className="sticky top-0 z-10 border-b"
            style={{
              borderColor: "rgba(51,65,85,0.6)",
              background: "rgba(30,41,59,0.55)",
              backdropFilter: "blur(10px)",
            }}
          >
            <tr>
              {columns.map((col, idx) => {
                const hide = col.hideOnMobile ? "hidden md:table-cell" : "";
                return (
                  <th
                    key={idx}
                    className={`px-6 py-4 text-xs font-semibold tracking-wide uppercase ${hide} ${
                      col.align || "text-left"
                    }`}
                    style={{
                      color: primaryColor,
                      width: col.width,
                      minWidth: col.minWidth,
                      whiteSpace: col.nowrap ? "nowrap" : undefined,
                    }}
                  >
                    {col.header}
                  </th>
                );
              })}
            </tr>
          </thead>

          <tbody className="divide-y divide-slate-800">
            {data.length > 0 ? (
              data.map((row, rowIdx) => {
                const key = getRowKey(row, rowIdx);
                const zebra = rowIdx % 2 === 0 ? "bg-slate-900/10" : "bg-slate-800/10";

                return (
                  <tr
                    key={key}
                    role={clickable ? "button" : undefined}
                    aria-label={clickable ? "Ver detalle" : undefined}
                    onClick={clickable ? () => onRowClick(row) : undefined}
                    tabIndex={clickable ? 0 : undefined}
                    onKeyDown={
                      clickable
                        ? (e) => {
                            if (e.key === "Enter" || e.key === " ") {
                              e.preventDefault();
                              onRowClick(row);
                            }
                          }
                        : undefined
                    }
                    className={[
                      zebra,
                      "transition-colors",
                      clickable ? "cursor-pointer hover:bg-slate-800/35" : "",
                      clickable
                        ? "focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-offset-slate-900"
                        : "",
                    ].join(" ")}
                    style={
                      clickable
                        ? {
                            // ring color via CSS variable
                            "--tw-ring-color": primaryColor,
                          }
                        : undefined
                    }
                  >
                    {columns.map((col, colIdx) => {
                      const hide = col.hideOnMobile ? "hidden md:table-cell" : "";
                      return (
                        <td
                          key={colIdx}
                          className={`px-6 py-4 ${hide} ${col.align || "text-left"} ${
                            col.className || "text-slate-200"
                          }`}
                          style={{
                            width: col.width,
                            minWidth: col.minWidth,
                            whiteSpace: col.nowrap ? "nowrap" : undefined,
                          }}
                        >
                          {col.render ? col.render(row[col.key], row) : row[col.key]}
                        </td>
                      );
                    })}
                  </tr>
                );
              })
            ) : (
              <tr>
                <td colSpan={columns.length} className="px-6 py-14">
                  <div className="flex flex-col items-center justify-center text-center">
                    <div
                      className="mb-3 grid place-items-center rounded-2xl border"
                      style={{
                        width: 48,
                        height: 48,
                        borderColor: `${primaryColor}30`,
                        backgroundColor: `${primaryColor}12`,
                        color: primaryColor,
                      }}
                      aria-hidden="true"
                    >
                      <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M9 12h6m-6 4h6M7 20h10a2 2 0 002-2V6a2 2 0 00-2-2H9l-2 2H7a2 2 0 00-2 2v10a2 2 0 002 2z"
                        />
                      </svg>
                    </div>

                    <p className="text-slate-300 font-medium">{emptyMessage}</p>
                    <p className="text-sm text-slate-500 mt-1">
                      Probá ajustar la búsqueda o creá un nuevo registro.
                    </p>
                  </div>
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
